import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { CalendarBlank, Trash, Clock, CheckCircle, XCircle, HourglassSimple, CurrencyEur, EnvelopeSimple, Printer, MagnifyingGlass, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';

const STATO_LABELS = {
  ricevuta: 'Ricevuta',
  accettata_da_pagare: 'Accettata (da pagare)',
  confermata_pagata: 'Confermata Pagata',
  rifiutata: 'Rifiutata',
  // legacy compat
  in_attesa: 'Ricevuta',
  confermata: 'Accettata (da pagare)',
};
const STATO_COLORS = {
  ricevuta: 'bg-amber-100 text-amber-800',
  accettata_da_pagare: 'bg-orange-200 text-orange-800',
  confermata_pagata: 'bg-green-100 text-green-800',
  rifiutata: 'bg-red-100 text-red-800',
  in_attesa: 'bg-amber-100 text-amber-800',
  confermata: 'bg-orange-200 text-orange-800',
};
const STATO_ICONS = {
  ricevuta: HourglassSimple,
  accettata_da_pagare: Clock,
  confermata_pagata: CheckCircle,
  rifiutata: XCircle,
  in_attesa: HourglassSimple,
  confermata: Clock,
};

export function BookingsSection() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [searchAlunno, setSearchAlunno] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('all');
  const [giornoFilter, setGiornoFilter] = useState('all');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [acceptDialog, setAcceptDialog] = useState(null); // {booking, templateName}
  const [resendDialog, setResendDialog] = useState(null); // {booking, templateName}
  const [resendingId, setResendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getBookings(filtro && filtro !== 'all' ? filtro : undefined);
      setBookings(res.data);
    } catch { toast.error('Errore caricamento prenotazioni'); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Seed di entrambi i template di sistema (Accettata + Prova)
    api.seedSystemTemplates().catch(() => {});
    // Carica lista template per il dialog di scelta
    api.getEmailTemplates().then(res => setEmailTemplates(res.data || [])).catch(() => {});
  }, []);

  const openAcceptDialog = (b) => {
    const suggested = b.is_prova ? 'Conferma Prenotazione Prova' : 'Conferma Prenotazione Accettata';
    setAcceptDialog({ booking: b, templateName: suggested, sendEmail: true });
  };

  const openResendDialog = (b) => {
    const suggested = b.last_resend_template || (b.is_prova ? 'Conferma Prenotazione Prova' : 'Conferma Prenotazione Accettata');
    setResendDialog({ booking: b, templateName: suggested, currentStudentEmail: b.current_student_email || '' });
  };

  const confirmResend = async () => {
    if (!resendDialog) return;
    const { booking, templateName } = resendDialog;
    setResendingId(booking.id);
    try {
      const res = await api.resendBookingConfirmation(booking.id, templateName);
      const emailUsed = res?.data?.email_used || booking.email;
      toast.success(`Conferma re-inviata a ${emailUsed}`);
      setResendDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore re-invio conferma');
    } finally {
      setResendingId(null);
    }
  };

  const confirmAccept = async () => {
    if (!acceptDialog) return;
    const { booking, templateName, sendEmail } = acceptDialog;
    try {
      const payload = { stato: 'accettata_da_pagare', send_email: !!sendEmail };
      if (sendEmail) payload.template_name = templateName;
      const res = await api.updateBooking(booking.id, payload);
      const w = res?.data?.workflow;
      const bits = [];
      if (w) {
        if (w.student_created) bits.push('alunno creato'); else if (w.student_id) bits.push('alunno collegato');
        if (w.lesson_id) bits.push('lezione creata');
        if (w.confirmation_id) bits.push('conferma iscrizione registrata');
        if (w.email_sent) bits.push('email inviata'); else if (w.email_error) bits.push('email non inviata'); else if (w.email_skipped) bits.push('email non richiesta');
      }
      toast.success(`Prenotazione accettata: ${bits.join(', ') || 'stato aggiornato'}`);
      setAcceptDialog(null);
      load();
    } catch { toast.error('Errore aggiornamento'); }
  };

  const handleUpdateStato = async (id, stato) => {
    try {
      const res = await api.updateBooking(id, { stato });
      if (stato === 'accettata_da_pagare' && res?.data?.workflow) {
        const w = res.data.workflow;
        const bits = [];
        if (w.student_created) bits.push('alunno creato'); else if (w.student_id) bits.push('alunno collegato');
        if (w.lesson_id) bits.push('lezione creata');
        if (w.confirmation_id) bits.push('conferma iscrizione registrata');
        if (w.email_sent) bits.push('email inviata'); else if (w.email_error) bits.push('email non inviata');
        toast.success(`Prenotazione accettata: ${bits.join(', ') || 'stato aggiornato'}`);
      } else {
        toast.success(`Prenotazione ${STATO_LABELS[stato].toLowerCase()}`);
      }
      load();
    } catch { toast.error('Errore aggiornamento'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa prenotazione?')) return;
    try {
      await api.deleteBooking(id);
      toast.success('Prenotazione eliminata');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleRemoveProva = async (b) => {
    if (!window.confirm(`Rimuovere il badge PROVA dalla prenotazione di ${b.nome}?\nSe è già stata accettata, la lezione collegata verrà aggiornata di conseguenza.`)) return;
    try {
      await api.setBookingProva(b.id, false);
      toast.success('Badge PROVA rimosso');
      load();
    } catch { toast.error('Errore rimozione PROVA'); }
  };

  const stateOf = (b) => {
    const s = b.stato;
    // Normalizza vecchi stati sui nuovi
    if (s === 'in_attesa') return 'ricevuta';
    if (s === 'confermata') return 'accettata_da_pagare';
    return s;
  };

  const DAY_NAMES = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
  const handlePrint = () => {
    const filterLabel = !filtro || filtro === 'all' ? 'Tutte' : STATO_LABELS[filtro] || filtro;
    // Applica i filtri client-side (alunno/insegnante/giorno) prima di stampare
    const preFiltered = bookings.filter(b => {
      if (searchAlunno.trim()) {
        const q = searchAlunno.trim().toLowerCase();
        if (!(b.nome || '').toLowerCase().includes(q)) return false;
      }
      if (teacherFilter !== 'all' && b.teacher_id !== teacherFilter) return false;
      if (giornoFilter !== 'all' && String(b.giorno) !== giornoFilter) return false;
      return true;
    });
    const rows = preFiltered.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const today = new Date().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return ''; } };
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Prenotazioni</title><style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 20px; color: #111; }
      header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #2D2A4A; padding-bottom: 8px; margin-bottom: 14px; }
      h1 { margin: 0; font-size: 18px; color: #2D2A4A; }
      .brand { font-size: 11px; color: #666; margin-top: 2px; }
      .meta { font-size: 10px; color: #666; text-align: right; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th, td { border: 1px solid #ddd; padding: 5px 6px; text-align: left; vertical-align: top; }
      th { background: #f4f4f5; font-size: 9px; text-transform: uppercase; letter-spacing: 0.4px; }
      td.alunno { font-weight: 600; text-transform: uppercase; }
      .prova { display: inline-block; padding: 1px 5px; background: #f59e0b; color: #fff; font-size: 8px; font-weight: 800; text-transform: uppercase; border-radius: 3px; letter-spacing: 0.4px; }
      .stato { font-weight: 700; }
      .stato-ricevuta { color: #b45309; }
      .stato-accettata_da_pagare { color: #c2410c; }
      .stato-confermata_pagata { color: #16a34a; }
      .stato-rifiutata { color: #b91c1c; }
      footer { margin-top: 24px; font-size: 10px; color: #999; text-align: center; }
      @page { size: A4 landscape; margin: 12mm; }
      @media print { body { margin: 0; } }
    </style></head><body>
    <header>
      <div>
        <h1>Prenotazioni - Filtro: ${escapeHtml(filterLabel)}</h1>
        <div class="brand">Fiori Musicali</div>
      </div>
      <div class="meta">Stampato il ${today}<br/>Totale: ${rows.length} prenotazioni</div>
    </header>
    <table>
      <thead><tr>
        <th style="width:26px">#</th>
        <th>Alunno</th>
        <th>Strumento</th>
        <th>Insegnante</th>
        <th>Giorno / Orario</th>
        <th>Durata</th>
        <th>Anno</th>
        <th>Email</th>
        <th>Tel.</th>
        <th>Data richiesta</th>
        <th>Stato</th>
      </tr></thead>
      <tbody>
        ${rows.map((b, i) => {
          const cs = stateOf(b);
          const day = DAY_NAMES[b.giorno] || '';
          const orario = `${day}${b.ora_inizio ? ' ' + b.ora_inizio : ''}`;
          const provaBadge = b.is_prova ? ' <span class="prova">Prova</span>' : '';
          return `<tr>
            <td>${i + 1}</td>
            <td class="alunno">${escapeHtml(b.nome)}${provaBadge}</td>
            <td>${escapeHtml(b.strumento || '-')}</td>
            <td>${escapeHtml(b.teacher_name || '-')}</td>
            <td>${escapeHtml(orario)}</td>
            <td>${escapeHtml(b.durata || '-')} min</td>
            <td>${escapeHtml(b.anno_accademico || '-')}</td>
            <td style="font-size:10px;color:#52525B">${escapeHtml(b.email || '-')}</td>
            <td>${escapeHtml(b.telefono || '-')}</td>
            <td>${fmtDate(b.created_at)}</td>
            <td class="stato stato-${cs}">${escapeHtml(STATO_LABELS[cs] || cs)}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
    <footer>Fiori Musicali &mdash; Prenotazioni &mdash; Pagina stampata il ${today}</footer>
    <script>window.onload = function() { setTimeout(function(){ window.print(); }, 200); }<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup bloccato dal browser'); return; }
    w.document.write(html);
    w.document.close();
  };
  const counts = {
    total: bookings.length,
    ricevuta: bookings.filter(b => stateOf(b) === 'ricevuta').length,
    accettata_da_pagare: bookings.filter(b => stateOf(b) === 'accettata_da_pagare').length,
    confermata_pagata: bookings.filter(b => stateOf(b) === 'confermata_pagata').length,
    rifiutata: bookings.filter(b => stateOf(b) === 'rifiutata').length,
  };

  // Elenco insegnanti derivato dalle prenotazioni
  const teacherOptions = Array.from(new Map(
    bookings.filter(b => b.teacher_id).map(b => [b.teacher_id, b.teacher_name || '—'])
  ).entries()).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));

  const DAY_NAMES_FILTER = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

  const filteredBookings = bookings.filter(b => {
    if (searchAlunno.trim()) {
      const q = searchAlunno.trim().toLowerCase();
      if (!(b.nome || '').toLowerCase().includes(q)) return false;
    }
    if (teacherFilter !== 'all' && b.teacher_id !== teacherFilter) return false;
    if (giornoFilter !== 'all' && String(b.giorno) !== giornoFilter) return false;
    return true;
  });

  return (
    <>
      <div className="mb-6 animate-fade-in flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Prenotazioni</h1>
          <p className="text-[#52525B] text-sm">Gestisci le richieste di prenotazione lezioni</p>
        </div>
        <Button variant="outline" onClick={handlePrint} className="rounded-sm" data-testid="bookings-print-btn">
          <Printer size={16} className="mr-2" />Stampa Lista
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4 animate-fade-in animate-delay-1">
        <Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger className="w-[220px] rounded-sm h-9 text-sm" data-testid="bookings-filter"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti ({counts.total})</SelectItem>
            <SelectItem value="ricevuta">Ricevute ({counts.ricevuta})</SelectItem>
            <SelectItem value="accettata_da_pagare">Accettate da Pagare ({counts.accettata_da_pagare})</SelectItem>
            <SelectItem value="confermata_pagata">Confermate Pagate ({counts.confermata_pagata})</SelectItem>
            <SelectItem value="rifiutata">Rifiutate ({counts.rifiutata})</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
          <Input
            value={searchAlunno}
            onChange={(e) => setSearchAlunno(e.target.value)}
            placeholder="Cerca alunno..."
            className="w-[180px] rounded-sm h-9 text-sm pl-7"
            data-testid="bookings-search-alunno"
          />
        </div>
        <Select value={teacherFilter} onValueChange={setTeacherFilter}>
          <SelectTrigger className="w-[200px] rounded-sm h-9 text-sm" data-testid="bookings-filter-teacher"><SelectValue placeholder="Insegnante" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli insegnanti</SelectItem>
            {teacherOptions.map(([tid, tname]) => <SelectItem key={tid} value={tid}>{(tname || '—').toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={giornoFilter} onValueChange={setGiornoFilter}>
          <SelectTrigger className="w-[150px] rounded-sm h-9 text-sm" data-testid="bookings-filter-day"><SelectValue placeholder="Giorno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i giorni</SelectItem>
            {DAY_NAMES_FILTER.slice(0, 5).map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        {(searchAlunno || teacherFilter !== 'all' || giornoFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { setSearchAlunno(''); setTeacherFilter('all'); setGiornoFilter('all'); }}
            className="text-xs text-[#71717A] hover:text-[#2D2A4A] underline"
            data-testid="bookings-clear-filters"
          >Reset filtri</button>
        )}
        <div className="flex gap-2 ml-auto items-center flex-wrap">
          <div className="text-xs text-[#52525B] font-['Work_Sans']" data-testid="bookings-results-count">{filteredBookings.length} / {bookings.length}</div>
          {counts.ricevuta > 0 && <div className="bg-amber-50 text-amber-700 px-3 py-1.5 rounded-sm text-sm font-medium">{counts.ricevuta} ricevute</div>}
          {counts.accettata_da_pagare > 0 && <div className="bg-orange-100 text-orange-800 px-3 py-1.5 rounded-sm text-sm font-medium">{counts.accettata_da_pagare} da pagare</div>}
        </div>
      </div>

      <div className="animate-fade-in animate-delay-2 space-y-3">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : filteredBookings.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm">
            <CalendarBlank size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">Nessuna prenotazione</p>
          </div>
        ) : filteredBookings.map(b => {
          const cs = stateOf(b);
          const Icon = STATO_ICONS[cs] || HourglassSimple;
          return (
            <div key={b.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`booking-${b.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm uppercase">{b.nome}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATO_COLORS[cs]}`}>
                      <Icon size={12} />{STATO_LABELS[cs]}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-[#52525B]">
                    <div><span className="text-[#A1A1AA]">Strumento:</span> {b.strumento}</div>
                    <div><span className="text-[#A1A1AA]">Insegnante:</span> {b.teacher_name}</div>
                    <div><span className="text-[#A1A1AA]">Giorno:</span> {b.giorno_nome} {b.ora_inizio}-{b.ora_fine}</div>
                    <div><span className="text-[#A1A1AA]">Durata:</span> {b.durata} min {b.is_prova && (
                      <button
                        type="button"
                        onClick={() => handleRemoveProva(b)}
                        className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500 hover:bg-amber-600 text-white text-[9px] font-extrabold uppercase tracking-wider rounded transition-colors cursor-pointer group"
                        title="Click per rimuovere il badge PROVA"
                        data-testid={`booking-prova-badge-${b.id}`}
                      >
                        PROVA<XCircle size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    )}</div>
                    <div><span className="text-[#A1A1AA]">Email:</span> {b.email}</div>
                    <div><span className="text-[#A1A1AA]">Tel:</span> {b.telefono || '-'}</div>
                    <div><span className="text-[#A1A1AA]">Anno:</span> {b.anno_accademico}</div>
                    <div><span className="text-[#A1A1AA]">Data:</span> {new Date(b.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap">
                  {cs === 'ricevuta' && (
                    <Button size="sm" onClick={() => openAcceptDialog(b)} className="bg-orange-500 hover:bg-orange-600 rounded-sm h-8 text-xs" data-testid={`accept-booking-${b.id}`}>
                      <Clock size={14} className="mr-1" />Accetta
                    </Button>
                  )}
                  {cs === 'accettata_da_pagare' && (
                    <Button size="sm" onClick={() => handleUpdateStato(b.id, 'confermata_pagata')} className="bg-green-600 hover:bg-green-700 rounded-sm h-8 text-xs" data-testid={`confirm-paid-${b.id}`}>
                      <CurrencyEur size={14} className="mr-1" />Segna Pagato
                    </Button>
                  )}
                  {(cs === 'accettata_da_pagare' || cs === 'confermata_pagata') && (
                    <Button
                      size="sm"
                      onClick={() => openResendDialog(b)}
                      disabled={resendingId === b.id}
                      className="bg-orange-500 hover:bg-orange-600 rounded-sm h-8 text-xs text-white"
                      data-testid={`resend-booking-${b.id}`}
                      title={b.last_resend_at ? `Ultimo re-invio: ${new Date(b.last_resend_at).toLocaleString('it-IT')}${b.last_resend_email ? ' a ' + b.last_resend_email : ''}` : 'Re-invia email di conferma'}
                    >
                      <PaperPlaneTilt size={14} className="mr-1" />{resendingId === b.id ? '...' : 'Re-invia'}
                    </Button>
                  )}
                  {cs !== 'rifiutata' && cs !== 'confermata_pagata' && (
                    <Button size="sm" variant="outline" onClick={() => handleUpdateStato(b.id, 'rifiutata')} className="rounded-sm h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-booking-${b.id}`}>
                      <XCircle size={14} className="mr-1" />Rifiuta
                    </Button>
                  )}
                  <button onClick={() => handleDelete(b.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-booking-${b.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!acceptDialog} onOpenChange={(open) => { if (!open) setAcceptDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Accetta Prenotazione</DialogTitle>
            <DialogDescription>
              {acceptDialog?.booking && (<>
                Confermi l'accettazione della prenotazione di <strong className="uppercase">{acceptDialog.booking.nome}</strong>
                {acceptDialog.booking.is_prova && <span className="ml-1 inline-block px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-extrabold uppercase tracking-wider rounded">PROVA</span>}
                ?
              </>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 bg-[#F4F4F5] rounded-sm">
              <input
                type="checkbox"
                id="accept-send-email"
                checked={!!acceptDialog?.sendEmail}
                onChange={(e) => setAcceptDialog(d => ({ ...d, sendEmail: e.target.checked }))}
                className="rounded"
                data-testid="accept-booking-send-email-checkbox"
              />
              <Label htmlFor="accept-send-email" className="text-sm cursor-pointer flex items-center gap-1.5 select-none">
                <EnvelopeSimple size={14} />
                Invia email di conferma all&apos;alunno
              </Label>
            </div>
            {acceptDialog?.sendEmail && (
              <>
                <Label className="label-overline text-xs flex items-center gap-1.5"><EnvelopeSimple size={13} />Modello Email da inviare</Label>
                <Select value={acceptDialog?.templateName || ''} onValueChange={(v) => setAcceptDialog(d => ({ ...d, templateName: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="accept-booking-template-select"><SelectValue placeholder="Scegli il modello email" /></SelectTrigger>
                  <SelectContent>
                    {emailTemplates.filter(t => (t.name || '').toLowerCase().includes('prenotazione') || (t.name || '').toLowerCase().includes('conferma')).map(t => (
                      <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                    ))}
                    {/* Fallback in caso i template non siano ancora caricati */}
                    {emailTemplates.length === 0 && (<>
                      <SelectItem value="Conferma Prenotazione Accettata">Conferma Prenotazione Accettata</SelectItem>
                      <SelectItem value="Conferma Prenotazione Prova">Conferma Prenotazione Prova</SelectItem>
                    </>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#71717A]">
                  {acceptDialog?.booking?.is_prova
                    ? 'Suggerito: "Conferma Prenotazione Prova" (lezione di prova gratuita).'
                    : 'Suggerito: "Conferma Prenotazione Accettata" (slot lezioni standard).'}
                </p>
              </>
            )}
            {!acceptDialog?.sendEmail && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                <strong>Nessuna email verrà inviata.</strong> Potrai comunque inviarla in un secondo momento con il pulsante &quot;Reinvia conferma&quot;.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptDialog(null)} className="rounded-sm" data-testid="accept-booking-cancel">Annulla</Button>
            <Button onClick={confirmAccept} disabled={acceptDialog?.sendEmail && !acceptDialog?.templateName} className="bg-orange-500 hover:bg-orange-600 rounded-sm" data-testid="accept-booking-confirm">
              <Clock size={14} className="mr-1" />{acceptDialog?.sendEmail ? 'Accetta e Invia' : 'Accetta senza email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resendDialog} onOpenChange={(open) => { if (!open) setResendDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Re-invia Conferma Prenotazione</DialogTitle>
            <DialogDescription>
              {resendDialog?.booking && (<>
                {(() => {
                  const bookingEmail = (resendDialog.booking.email || '').trim().toLowerCase();
                  const anagEmail = (resendDialog.currentStudentEmail || '').trim().toLowerCase();
                  const finalEmail = resendDialog.currentStudentEmail || resendDialog.booking.email;
                  const emailChanged = anagEmail && bookingEmail && anagEmail !== bookingEmail;
                  return (
                    <>
                      Re-invia l'email di conferma per la prenotazione di <strong className="uppercase">{resendDialog.booking.nome}</strong>.
                      <span className="block mt-2 text-sm">
                        <span className="text-[#71717A]">Destinatario: </span>
                        <strong className="text-[#0A0A0A]">{finalEmail || '—'}</strong>
                      </span>
                      {emailChanged && (
                        <span className="block mt-1 text-xs px-2 py-1 bg-green-50 border border-green-200 rounded text-green-800">
                          Verrà usata la mail aggiornata in anagrafica alunno (vecchia sul booking: {resendDialog.booking.email})
                        </span>
                      )}
                      {!anagEmail && bookingEmail && (
                        <span className="block mt-1 text-xs text-[#A1A1AA]">Nessuna email trovata in anagrafica alunno, verrà usata quella salvata sulla prenotazione.</span>
                      )}
                      {resendDialog.booking.last_resend_at && (
                        <span className="block text-xs text-[#71717A] mt-2">
                          Ultimo re-invio: {new Date(resendDialog.booking.last_resend_at).toLocaleString('it-IT')}
                          {resendDialog.booking.last_resend_email ? ` a ${resendDialog.booking.last_resend_email}` : ''}
                        </span>
                      )}
                      <span className="block text-xs text-[#71717A] mt-1">Non modifica lo stato della prenotazione né la lezione collegata.</span>
                    </>
                  );
                })()}
              </>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="label-overline text-xs flex items-center gap-1.5"><EnvelopeSimple size={13} />Modello Email da inviare</Label>
            <Select value={resendDialog?.templateName || ''} onValueChange={(v) => setResendDialog(d => ({ ...d, templateName: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="resend-booking-template-select"><SelectValue placeholder="Scegli il modello email" /></SelectTrigger>
              <SelectContent>
                {emailTemplates.filter(t => (t.name || '').toLowerCase().includes('prenotazione') || (t.name || '').toLowerCase().includes('conferma')).map(t => (
                  <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                ))}
                {emailTemplates.length === 0 && (<>
                  <SelectItem value="Conferma Prenotazione Accettata">Conferma Prenotazione Accettata</SelectItem>
                  <SelectItem value="Conferma Prenotazione Prova">Conferma Prenotazione Prova</SelectItem>
                </>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendDialog(null)} className="rounded-sm" data-testid="resend-booking-cancel">Annulla</Button>
            <Button onClick={confirmResend} disabled={!resendDialog?.templateName || resendingId === resendDialog?.booking?.id} className="bg-orange-500 hover:bg-orange-600 rounded-sm text-white" data-testid="resend-booking-confirm">
              <PaperPlaneTilt size={14} className="mr-1" />{resendingId === resendDialog?.booking?.id ? 'Invio in corso...' : 'Re-invia Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
